import React, { useContext, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Link,
  Box,
  IconButton,
  InputGroup,
  InputRightElement,
  Flex, Alert, AlertIcon,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import ReCAPTCHA from 'react-google-recaptcha';
import fetchData from './utils/Util';
import { ConfigContext } from './utils/Config'; // Ensure this is the correct path

const Login = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const config = useContext(ConfigContext);

  if (!config) {
    return <div>Loading...</div>;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Encode credentials in Base64
    const credentials = btoa(`${formData.email}:${formData.password}`);
    try {
      const response = await fetchData(`${config.backendUrl}/api/login-token?site=${config.siteName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
        },
      });
  
      if (response.ok) {
        const data = await response.json();
        // Handle successful login (reset error message)
        setErrorMessage('');
        // Call a separate function to update the access token and login status
        handleLoginSuccess(data.access_token);
      } else {
        // Map backend errors to translation keys
        let errorKey = 'login.errors.badCredentials';
        setErrorMessage(errorKey);
      }
    } catch (error) {
      setErrorMessage('login.errors.networkError');
    }
  };

  const handleLoginSuccess = async (accessToken) => {
    // Save access_token to local storage, and load user data.
    await config.setAccessToken(accessToken);
    // Dispatch a custom event to indicate successful login
    window.dispatchEvent(new Event('userLoggedIn'));
    // Call the onLogin function to update the authentication status
    if (onLogin) {
      onLogin();
    }
    // Get the redirect URL from the query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get('redirect');

    // Redirect to the specified URL or default to the home page
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.location.href = '/';
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (!config) {
    return <div>Loading...</div>;
  }

  if (config.success === false) {
    return (
      <Alert status="error" marginBottom="20px">
        <AlertIcon />
        Cannot access site settings.
      </Alert>
    );
  }

  return (
    <Box className="user-login">
       <h1>{t('login.title')}</h1>
      <Box className="instructions">
        <Text>
         {t('login.emailInstructions')}
        </Text>
        <Text>
          {t('login.passwordInstructions')}
        </Text>
      </Box>
      <form onSubmit={handleSubmit}>        
        <FormControl marginBottom="20px" isRequired>
          <FormLabel>{t('login.email')}</FormLabel>
          <InputGroup>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('login.emailPlaceholder')}
              required
            />
         </InputGroup>
        </FormControl>
        <FormControl marginBottom="20px" isRequired>
          <FormLabel>{t('login.password')}</FormLabel>
          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('login.passwordPlaceholder')}
            />
            <InputRightElement width="3rem">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                onClick={togglePasswordVisibility}
                icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>
        <FormControl marginBottom="20px" isRequired>
          <Flex alignItems="center">
            <input
              type="checkbox"
              id="termsCheckbox"
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
              style={{ marginRight: '10px' }}
              required
            />
            <FormLabel htmlFor="termsCheckbox">
              <Trans i18nKey="register.termsAcceptance">
                I have read and accepted the Auctioneers <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="/conditions" target="_blank" rel="noopener noreferrer">Data Privacy Terms</a> and I agree to abide by them.
              </Trans>
            </FormLabel>
          </Flex>
        </FormControl>
        <Box marginBottom="30px">
          <ReCAPTCHA
            sitekey={config.recaptchaKey}
            hl={i18n.language} // Pass the language code here
            onChange={(token) => console.log(token)}
          />
        </Box>
        <Button className="submit" type="submit" colorScheme="blue" isDisabled={!termsAccepted}>
          {t('login.loginButton')}
        </Button>

        {errorMessage && (
          <Alert status="error" marginTop="20px" borderRadius="md">
            <AlertIcon />
            {t('login.errors.badCredentials', errorMessage)}
          </Alert>
        )}

      </form>
      <Text className="forgot-password-link">
        <Link href="/forgot-password">{t('login.forgotPassword')}</Link>
      </Text>
    </Box>
  );
};

export default Login;